import { Controller } from "stimulus";

const PER_PAGE = 25;

// Infinite-scroll for the Pro finances income list. Mirrors the disabled
// scheduling report: an IntersectionObserver watches a sentinel div and
// fetches the next page of rows, appending them to the table body.
export default class extends Controller {
  static targets = ["tableBody", "loadingIndicator", "scrollTrigger"];

  connect() {
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMore = false;

    if (this.hasScrollTriggerTarget) {
      const trigger = this.scrollTriggerTarget;
      this.currentPage = parseInt(trigger.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(trigger.dataset.totalPages, 10) || 1;
      this.perPage = parseInt(trigger.dataset.perPage, 10) || PER_PAGE;
      this.year = trigger.dataset.year || "";
      this.hasMore = this.currentPage < this.totalPages;

      if (this.hasMore) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && this.hasMore && !this.isLoading) {
                this.loadNextPage();
              }
            });
          },
          {
            rootMargin: "200px",
            threshold: 0.1,
          }
        );

        this.observer.observe(trigger);
      }
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta && meta.content;
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMore) return;

    this.isLoading = true;
    this.currentPage += 1;

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = "block";
    }

    try {
      let url = `/pro/web/finances/page?page=${this.currentPage}`;
      if (this.year) {
        url += `&year=${encodeURIComponent(this.year)}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/html",
          "X-CSRF-Token": this.csrfToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load page: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      const tempTableBody = document.createElement("tbody");
      tempTableBody.innerHTML = html.trim();

      const rows = Array.from(tempTableBody.querySelectorAll("tr"));

      if (rows.length === 0) {
        this.hasMore = false;
        if (this.hasScrollTriggerTarget) {
          this.scrollTriggerTarget.style.display = "none";
        }
      } else {
        if (this.hasTableBodyTarget) {
          rows.forEach((row) => {
            this.tableBodyTarget.appendChild(row);
          });
        }

        if (rows.length < this.perPage) {
          this.hasMore = false;
          if (this.hasScrollTriggerTarget) {
            this.scrollTriggerTarget.style.display = "none";
          }
        } else if (this.hasScrollTriggerTarget) {
          this.scrollTriggerTarget.dataset.currentPage = String(this.currentPage);
        }
      }
    } catch (error) {
      console.error("Error loading next page:", error);
      this.currentPage -= 1;
    } finally {
      this.isLoading = false;
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.style.display = "none";
      }
    }
  }
}
